/**
 * ADMET configured-good-range metadata + classifier (#89, Tier 1).
 *
 * PURE module — no DOM, no `App`. The canonical MapLight ADMET metadata lives in
 * `jobman/jobs/MaplightJob.js` (`scopedPropertyMetadata`), but that file imports
 * `BMapsApp` and cannot be loaded headlessly. This module mirrors ONLY the
 * `goodRange` strings (+ label) for the 22 MapLight endpoints so the headless
 * `admet-properties` evaluator can flag out-of-range values without dragging the
 * browser app into the engine.
 *
 * Jessica (#89) noted the property tooltips already carry configured good ranges — so
 * range checking is NOT blocked on an SME; it reuses metadata the product
 * already ships. This table is a manual mirror of MaplightJob.js (which can't be
 * imported here); keep the two in sync if the MapLight metadata changes.
 *
 * `goodRange` grammar observed across all 22 endpoints (100% coverage):
 *   - `<X`      → good when value <  X   (e.g. hERG `<0.5`)
 *   - `>X`      → good when value >  X   (e.g. HIA  `>0.5`)
 *   - `A - B`   → good when A <= value <= B (e.g. logP `1 - 3`)
 *
 * @module gibbs/autonomousCampaign/evaluators/admetRanges
 */

/**
 * Masked-safe metadata: endpoint key → { label, goodRange, unit }. Mirrors
 * MaplightJob.js `scopedPropertyMetadata.compound.childGroups.maplight.properties`.
 * @type {Object<string, {label: string, goodRange: string, unit: string}>}
 */
export const ADMET_RANGE_METADATA = Object.freeze({
    ADMET_ames: { label: 'Ames Mutagenicity', goodRange: '<0.5', unit: 'Probability' },
    ADMET_bbb_martins: { label: 'Blood-Brain Barrier Permeability', goodRange: '>0.5', unit: 'Probability' },
    ADMET_bioavailability_ma: { label: 'Oral Bioavailability (F%)', goodRange: '>0.5', unit: 'Probability' },
    ADMET_caco2_wang: { label: 'Caco-2 Permeability (Papp)', goodRange: '>10', unit: '10^-6 cm/s' },
    ADMET_clearance_hepatocyte_az: { label: 'Hepatocyte Clearance', goodRange: '13 - 31', unit: 'mL/min/kg' },
    ADMET_clearance_microsome_az: { label: 'Microsome Clearance', goodRange: '13 - 31', unit: 'mL/min/kg' },
    ADMET_cyp2c9_substrate_carbonmangels: { label: 'CYP2C9 Substrate', goodRange: '<0.5', unit: 'Probability' },
    ADMET_cyp2c9_veith: { label: 'CYP2C9 Inhibition', goodRange: '<0.5', unit: 'Probability' },
    ADMET_cyp2d6_substrate_carbonmangels: { label: 'CYP2D6 Substrate', goodRange: '<0.5', unit: 'Probability' },
    ADMET_cyp2d6_veith: { label: 'CYP2D6 Inhibition', goodRange: '<0.5', unit: 'Probability' },
    ADMET_cyp3a4_substrate_carbonmangels: { label: 'CYP3A4 Substrate', goodRange: '<0.5', unit: 'Probability' },
    ADMET_cyp3a4_veith: { label: 'CYP3A4 Inhibition', goodRange: '<0.5', unit: 'Probability' },
    ADMET_dili: { label: 'Drug-Induced Liver Injury Risk', goodRange: '<0.5', unit: 'Probability' },
    ADMET_half_life_obach: { label: 'Half-Life', goodRange: '12 - 48', unit: 'hours' },
    ADMET_herg: { label: 'hERG Inhibition', goodRange: '<0.5', unit: 'Probability' },
    ADMET_hia_hou: { label: 'Human Intestinal Absorption (HIA)', goodRange: '>0.5', unit: 'Probability' },
    ADMET_ld50_zhu: { label: 'LD50', goodRange: '>2000', unit: 'mg/kg' },
    ADMET_lipophilicity_astrazeneca: { label: 'Lipophilicity', goodRange: '1 - 3', unit: 'logP' },
    ADMET_pgp_broccatelli: { label: 'P-gp Inhibition', goodRange: '>0.5', unit: 'Probability' },
    ADMET_ppbr_az: { label: 'Plasma Protein Binding', goodRange: '<90', unit: '% bound' },
    ADMET_solubility_aqsoldb: { label: 'Aqueous solubility', goodRange: '>4', unit: 'g/mL' },
    ADMET_vdss_lombardo: { label: 'Volume of Distribution (VDss)', goodRange: '0.2 - 0.7', unit: 'L/kg' },
});

/**
 * Parse a `goodRange` string into a predicate over a numeric value. Returns null
 * for an unrecognized grammar (so the caller treats that endpoint as "unknown"
 * rather than guessing).
 * @param {string} goodRange
 * @returns {((value:number)=>boolean)|null}
 */
export function parseGoodRange(goodRange) {
    if (typeof goodRange !== 'string') return null;
    const s = goodRange.trim();

    const lt = s.match(/^<\s*(-?\d+(?:\.\d+)?)$/);
    if (lt) {
        const bound = Number(lt[1]);
        return (v) => v < bound;
    }
    const gt = s.match(/^>\s*(-?\d+(?:\.\d+)?)$/);
    if (gt) {
        const bound = Number(gt[1]);
        return (v) => v > bound;
    }
    const band = s.match(/^(-?\d+(?:\.\d+)?)\s*-\s*(-?\d+(?:\.\d+)?)$/);
    if (band) {
        const lo = Number(band[1]);
        const hi = Number(band[2]);
        return (v) => v >= lo && v <= hi;
    }
    return null;
}

/**
 * Classify one ADMET value against its endpoint's configured good range.
 * @param {string} key - the `ADMET_*` endpoint key.
 * @param {number} value
 * @returns {'good'|'flag'|'unknown'} `unknown` = no range metadata / unparsable /
 *   non-finite value (never guess); `flag` = outside the configured good range.
 */
export function classifyAdmetValue(key, value) {
    if (!Number.isFinite(value)) return 'unknown';
    const meta = ADMET_RANGE_METADATA[key];
    if (!meta) return 'unknown';
    const predicate = parseGoodRange(meta.goodRange);
    if (!predicate) return 'unknown';
    return predicate(value) ? 'good' : 'flag';
}
